import { Paginate, PaginationComponent } from '@circe/pagination';
import { BehaviorSubject, combineLatest, isObservable, Observable, of, Subject, Subscription } from 'rxjs';
import { NpaId, ToolService } from '@circe/core';
import { map, takeUntil } from 'rxjs/operators';
import { NpaTableFilter, NpaTableSort } from './table.types';

export class TableSource<T> {
  public totalItems$: BehaviorSubject<number> = new BehaviorSubject<number>(0);


  public get data(): T[] | BehaviorSubject<T[]> {
    return this._data$;
  }

  public set data(data: T[] | BehaviorSubject<T[]>) {
    if (data) {
      if (isObservable(data)) {
        this._dataSubscription?.unsubscribe();
        this._dataSubscription = data.subscribe((data: T[]) => this._data$.next(data));
      } else {
        this._data$.next(data);
      }
    }
  }

  public get filter(): NpaTableFilter[] | BehaviorSubject<NpaTableFilter[]> {
    return this._filter$;
  }

  public set filter(filter: NpaTableFilter[] | BehaviorSubject<NpaTableFilter[]>) {
    if (filter) {
      if (isObservable(filter)) {
        this._filterSubscription?.unsubscribe();
        this._filterSubscription = filter.subscribe((filter: NpaTableFilter[]) => this._filter$.next(filter));
      } else {
        this._filter$.next(filter);
      }
    }
  }

  public get sorter(): NpaTableSort | BehaviorSubject<NpaTableSort> {
    return this._sorter$;
  }

  public set sorter(sorter: NpaTableSort | BehaviorSubject<NpaTableSort>) {
    if (sorter) {
      if (isObservable(sorter)) {
        this._sortSubscription?.unsubscribe();
        this._sortSubscription = sorter.subscribe((sort: NpaTableSort) => this._sorter$.next(sort));
      } else {
        this._sorter$.next(sorter);
      }
    }
  }

  public get paginator(): Paginate | BehaviorSubject<Paginate> {
    return this._paginator$;
  }

  public set paginator(paginator: Paginate | BehaviorSubject<Paginate>) {
    if (paginator) {
      if (isObservable(paginator)) {
        this._pageSubscription?.unsubscribe();
        this._pageSubscription = paginator.subscribe((page: Paginate) => this._paginator$.next(page));
      } else {
        this._paginator$.next(paginator);
      }
    }
  }

  private _data$: BehaviorSubject<T[]> = new BehaviorSubject([]);
  private _filter$: BehaviorSubject<NpaTableFilter[]> = new BehaviorSubject<NpaTableFilter[]>([]);
  private _sorter$: BehaviorSubject<NpaTableSort> = new BehaviorSubject<NpaTableSort>(null);
  private _paginator$: BehaviorSubject<Paginate> = new BehaviorSubject<Paginate>(null);
  private _renderChanges$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  private cancelStream$: Subject<void> = new Subject<void>();
  private _renderChangesSubscription: Subscription;
  private _dataSubscription: Subscription;
  private _filterSubscription: Subscription;
  private _sortSubscription: Subscription;
  private _pageSubscription: Subscription;

  constructor(
    data: T[] | BehaviorSubject<T[]>,
    _filter?: NpaTableFilter[] | BehaviorSubject<NpaTableFilter[]>,
    _paginator?: Paginate | BehaviorSubject<Paginate>,
    _sorter?: NpaTableSort | BehaviorSubject<NpaTableSort>
  ) {
    if (data) {
      this.data = data;
    }

    if (_filter) {
      this.filter = _filter;
    }

    if (_sorter) {
      this.sorter = _sorter;
    }

    if (_paginator) {
      this.paginator = _paginator;
    }

    this._getDataObservable();
  }

  public static sortRows(rows: any[], columnId: NpaId, order: 'asc' | 'desc' = 'asc'): any[] {
    const _output: any[] = rows.map((e: any) => {
      return (e[columnId] === undefined) ? { ...e, [columnId]: '' } : (e[columnId] === 0) ? { ...e, [columnId]: '0' } : e;
    }).sort((a: any, b: any) => (order === 'asc') ? (a[columnId] > b[columnId] ? 1 : -1) : (a[columnId] < b[columnId] ? 1 : -1));
    return _output;
  }

  public static filterRows(rows: any[], filters: NpaTableFilter[]): any[] {
    const _output: any[] = rows
      .filter((row: any) => {
        const _filterSuccess: boolean = filters.filter((f: NpaTableFilter) => !!f.columns && !!f.value).every((f: NpaTableFilter) => {
          return f.columns.some((columnId: NpaId) => {
            const _value: string = ToolService.getValueFromMultiLevelObject(row, String(columnId)).toString().toLowerCase();
            const _filterValue: string = f.value.toString().toLowerCase();
            return f.searchExactly ? _value === _filterValue : _value.includes(_filterValue);
          });
        });
        return _filterSuccess;
      });
    return _output;
  }

  private _getDataObservable(): void {
    let _dataStream: Observable<T[]> = isObservable(this._data$) ?
      this._data$ : new BehaviorSubject<T[]>(this._data$);
    let _filterStream: Observable<NpaTableFilter[]> = isObservable(this._filter$) ?
      this._filter$ : new BehaviorSubject<NpaTableFilter[]>(this._filter$);
    let _sortStream: Observable<NpaTableSort> = isObservable(this._sorter$) ?
      this._sorter$ : new BehaviorSubject<NpaTableSort>(this._sorter$);
    let _pageStream: Observable<Paginate> = isObservable(this._paginator$) ?
      this._paginator$ : new BehaviorSubject<Paginate>(this._paginator$);

    this.cancelStream$.next();
    _dataStream = _dataStream.pipe(takeUntil(this.cancelStream$));
    _filterStream = _filterStream.pipe(takeUntil(this.cancelStream$));
    _sortStream = _sortStream.pipe(takeUntil(this.cancelStream$));
    _pageStream = _pageStream.pipe(takeUntil(this.cancelStream$));

    const _filteredData: Observable<T[]> = combineLatest([_dataStream, _filterStream])
      .pipe(map(([data, filters]: [T[], NpaTableFilter[]]) => {
        return this._filterRows(data, filters);
      }));
    const _orderedData: Observable<T[]> = combineLatest([_filteredData, _sortStream])
      .pipe(map(([data, sort]: [T[], NpaTableSort]) => {
        return this._orderRows(data, sort);
      }));

    const _paginatedData: Observable<T[]> = combineLatest([_orderedData, _pageStream])
      .pipe(map(([data, page]: [T[], Paginate]) => {
        return this._pageRows(data, page);
      }));

    this._renderChangesSubscription?.unsubscribe();
    this._renderChangesSubscription = _paginatedData.subscribe((d: T[]) => {
      this._renderChanges$.next(d);
    });
  }

  private _filterRows(data: T[], filters: NpaTableFilter[]): T[] {
    if (!filters?.length) {
      this.totalItems$.next(data.length);
      return data;
    }

    const _filteredData: T[] = TableSource.filterRows(data, filters);
    const _actualPage: Paginate = isObservable(this._paginator$) ? this._paginator$.value : this._paginator$;
    const _newPage: Paginate = { pageFirstItem: 0, itemsPerPage: _actualPage?.itemsPerPage };
    if (this._paginator$) {
      this._paginator$.next(_newPage);
      this.totalItems$.next(_filteredData.length);
    }

    return _filteredData;
  }

  private _orderRows(data: T[], sort: NpaTableSort): T[] {
    if (!sort || !sort.columnId || !sort.sortType) {
      return data;
    }
    return [...TableSource.sortRows(data.slice(), sort.columnId, sort.sortType)];
  }

  private _pageRows(data: T[], page: Paginate): T[] {
    if (!!page && !!page.itemsPerPage && page.pageFirstItem >= 0) {
      const startIndex: number = page.pageFirstItem;
      return data.slice(startIndex, startIndex + (page.itemsPerPage));
    }

    return data;
  }

  private _isPaginationComponent(obj: any): obj is PaginationComponent {
    return !!obj && (obj instanceof PaginationComponent || (typeof obj.ngOnInit === 'function' && typeof obj.ngOnChanges === 'function'));
  }

  public connect(): Observable<T[]> {
    return this._renderChanges$;
  }
}

export function _isTableSource<T>(obj: any): obj is TableSource<T> {
  return !!obj && (obj instanceof TableSource || (typeof obj.connect === 'function'));
}
